import React from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router';

import RowCell from 'components/Grids/DataGrid/Cells/RowCell';
import GridActions from 'components/Grids/GridActions';
import Popover from 'components/Popover';
import { ICONS_MAP } from 'constants/icons';
import { appReactMemo } from 'hocs';
import { useStatePath } from 'hooks/useStatePath';
import { ROUTES } from 'routes/config/constants';

import { BLACKLIST_CARD_MASK_DATA_VARIANT } from '../../../TrafficManagement/Blacklists/CardMasks/CardMask/Form/helpers';
import { BLACKLIST_PANS_DATA_VARIANT } from '../../../TrafficManagement/Blacklists/CardPans/CardPan/Form/helpers';
import ChangeInstrumentStatus from '../../ChangeInstrumentStatus';
import { Container } from '../../ChangeInstrumentStatus/styled';

import { TClientInstrumentRowCellProps } from './types';

interface IActionsCellProps extends TClientInstrumentRowCellProps {
  actionsCount: number;
  canBlockedByCardMask: boolean;
  canBlockedByPan: boolean;
  canChangeStatus: boolean;
}

const ActionsCell = ({
  actionsCount,
  canBlockedByCardMask,
  canBlockedByPan,
  canChangeStatus,
  ...params
}: IActionsCellProps) => {
  const { t } = useTranslation('translation');
  const navigate = useNavigate();
  const statePath = useStatePath();

  const {
    blockedByCardMask,
    blockedByPan,
    cardPanId,
    customer,
    merchantTerminal,
    verification,
  } = params.item;

  const onBlockedByPan = React.useCallback(() => {
    navigate(
      `${generatePath(
        ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_PAN,
      )}?${new URLSearchParams({
        cardPanId: cardPanId?.toString() || '',
        customerId: customer?.merchantCustomerId?.toString() || '',
        merchantTerminalId: merchantTerminal?.id?.toString() || '',
        variant: BLACKLIST_PANS_DATA_VARIANT.CLIENT_INSTRUMENTS_DATA,
      }).toString()}`,
      { state: statePath },
    );
  }, [cardPanId, customer, merchantTerminal, navigate, statePath]);

  const onBlockedByCardMask = React.useCallback(() => {
    navigate(
      `${generatePath(
        ROUTES.TRAFFIC_MANAGEMENT.SUB_PATH.BLACKLISTS.SUB_PATH.CREATE_MASK,
      )}?${new URLSearchParams({
        cardPanId: cardPanId?.toString() || '',
        customerId: customer?.merchantCustomerId?.toString() || '',
        merchantTerminalId: merchantTerminal?.id?.toString() || '',
        variant: BLACKLIST_CARD_MASK_DATA_VARIANT.CLIENT_INSTRUMENTS_DATA,
      }).toString()}`,
      { state: statePath },
    );
  }, [cardPanId, customer, merchantTerminal, navigate, statePath]);

  const config = React.useMemo(
    () => [
      {
        available: canBlockedByPan,
        disabled: blockedByPan,
        onClick: onBlockedByPan,
        text: t('clientsInstruments.blockedByPan'),
      },
      {
        available: canBlockedByCardMask,
        disabled: blockedByCardMask,
        onClick: onBlockedByCardMask,
        text: t('clientsInstruments.blockedByCardMask'),
      },
    ],
    [
      blockedByCardMask,
      blockedByPan,
      canBlockedByCardMask,
      canBlockedByPan,
      onBlockedByCardMask,
      onBlockedByPan,
      t,
    ],
  );

  return (
    <RowCell {...params} center>
      <Container>
        {canChangeStatus && (
          <Popover
            component={
              <ChangeInstrumentStatus
                cardPanId={cardPanId}
                customerId={customer.id}
                verification={verification}
              />
            }
          >
            <div>
              <ICONS_MAP.Edit width={18} />
            </div>
          </Popover>
        )}
        <GridActions
          actionsCount={actionsCount}
          config={config}
          popoverDisplayLimit={1}
        />
      </Container>
    </RowCell>
  );
};

export default appReactMemo(ActionsCell);
